// src/components/reports/SupervisorReport/SupervisorCustomerChart.tsx
import React from 'react';
import {
    ResponsiveContainer,
    ComposedChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend
} from 'recharts';
import { Box, Paper, Typography } from '@mui/material';
import { SupervisorCustomerReportItem, formatCurrency } from '../../../../types/sales';

interface SupervisorCustomerChartProps {
    data: SupervisorCustomerReportItem[];
    title?: string;
    fullSize?: boolean; // 追加: フルサイズでグラフを表示するかどうか
    selectedSupervisor?: string; // 追加: 選択された担当者名
}

export const SupervisorCustomerChart: React.FC<SupervisorCustomerChartProps> = ({ 
    data, 
    title,
    fullSize = false,
    selectedSupervisor 
}) => {
    // データから合計行を除外し、顧客別データのみを表示
    const filteredData = data.filter(item => 
        // 合計行を除外（'Total'キーワードまたは特定のフラグによる判定）
        item.supervisor_name !== '合計' && 
        item.customer_name !== '合計' &&
        // 選択された担当者がある場合はその担当者のデータのみをフィルタリング
        (!selectedSupervisor || item.supervisor_name === selectedSupervisor)
    );


    // グラフ用データの準備
    const chartData = filteredData.map(item => ({
        name: item.customer_name || '-', // 顧客名を表示
        売上合計: Number(item.total_sales),
        粗利益: Number(item.gross_profit),
        マイナス: item.final_profit < 0 ? Math.abs(Number(item.final_profit)) : 0,
        最終利益: item.final_profit > 0 ? Number(item.final_profit) : 0,
        // ツールチップ用の追加データ
        supervisorName: item.supervisor_name || '-'
    }));

    // カスタムツールチップ
    const CustomTooltip = ({ active, payload, label }: any) => {
        if (active && payload && payload.length) {
            return (
                <Paper elevation={3} sx={{ p: 2, bgcolor: 'background.paper' }}>
                    <Typography variant="subtitle2">{label}</Typography>
                    {selectedSupervisor ? null : (
                        <Typography variant="body2" color="textSecondary" gutterBottom>
                            {payload[0]?.payload.supervisorName}
                        </Typography>
                    )}
                    {payload.map((entry: any, index: number) => (
                        <Typography 
                            key={`tooltip-${index}`} 
                            variant="body2" 
                            sx={{ 
                                color: entry.color,
                                display: 'flex',
                                justifyContent: 'space-between',
                                mb: 0.5
                            }}
                        >
                            <span style={{ marginRight: '12px' }}>{entry.name}:</span>
                            <span style={{ fontWeight: 'bold' }}>
                                {formatCurrency(entry.value)}
                            </span>
                        </Typography>
                    ))}
                </Paper>
            );
        }
        return null;
    };

    // サイズの設定 - fullSizeフラグに基づいて高さを変更
    const height = fullSize ? 500 : 400;

    return (
        <Box sx={{ width: '100%', height: 'auto', mt: 3 }}>
            {title && (
                <Typography variant="h6" sx={{ mb: 1, textAlign: 'center' }} className="chart-title">
                    {title}
                </Typography>
            )}
            <Paper 
                variant="outlined" 
                sx={{ 
                    p: 2, 
                    height: 'auto',
                    minHeight: height,
                    bgcolor: 'background.paper',
                    textAlign: 'center'
                }}
                className="chart-container"
            >
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'center' }}>
                    <ResponsiveContainer width="100%" height={height}>
                        <ComposedChart
                            data={chartData}
                            margin={{
                                top: 15,
                                right: 30,
                                left: 40,
                                bottom: 10, // 凡例のためにボトムマージンを増やす
                            }}
                        >
                            <CartesianGrid strokeDasharray="3 3" />
                            <XAxis
                                dataKey="name"
                                angle={0}
                                textAnchor="end"
                                height={10}
                                tickMargin={20}
                                tick={{ fontSize: 14 }}
                            />
                            <YAxis
                                orientation="left"
                                tickFormatter={(value) => `¥${(value / 10000).toFixed(0)}万`}
                                tick={{ fontSize: 14 }}
                                label={{
                                    value: '金額（円）',
                                    angle: -90,
                                    position: 'insideLeft',
                                    offset: -30,
                                    style: { textAnchor: 'middle', fontSize: 14 }
                                }}
                            />
                            <Tooltip content={<CustomTooltip />} />
                            <Legend
                                wrapperStyle={{
                                    bottom: -60,
                                    left: 0,
                                    right: 0,
                                    fontSize: 14,
                                    padding: '0 10px',
                                    width: '100%',
                                    display: 'flex',
                                    justifyContent: 'center'
                                }}
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                                iconSize={16}
                                iconType="square"
                            />
                            <Bar
                                dataKey="売上合計"
                                name="売上合計"
                                barSize={40}
                                fill="#4CAF50"
                            />
                            <Bar
                                dataKey="粗利益"
                                name="粗利益"
                                barSize={40}
                                fill="#2196F3"
                            />
                            <Bar
                                dataKey="マイナス"
                                name="マイナス (赤字額)"
                                barSize={40}
                                fill="#F44336"
                            />
                        </ComposedChart>
                    </ResponsiveContainer>
                </Box>
            </Paper>
        </Box>
    );
};